import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Package, LogOut, ClipboardCheck, Star, Play, Square, Trash2, RotateCcw,
  ChevronRight, CheckCircle, XCircle, AlertTriangle, Plus
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { AddTireForm as SharedAddTireForm, useFormValues } from "../components/TireFormShared";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const DIAMETERS = [13,14,15,16,17,18,19,20,21,22,23,24];

// ==========================================
// STEP 1 — FILTERS
// ==========================================
const FiltersStep = ({ onStart }) => {
  const [tireType, setTireType] = useState("new");
  const [diameter, setDiameter] = useState("all");
  const [category, setCategory] = useState("pairs");

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4" data-testid="inventory-filters">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#7C3AED] rounded-xl flex items-center justify-center mx-auto mb-4">
            <ClipboardCheck className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-[#0A0A0A]">Nouvel inventaire</h2>
          <p className="text-sm text-[#525252] mt-1">Sélectionnez les filtres avant de commencer</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="input-label">Type de pneu</label>
            <Select value={tireType} onValueChange={setTireType}>
              <SelectTrigger className="input-field h-12 text-base" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Neuf</SelectItem>
                <SelectItem value="used">Occasion</SelectItem>
                <SelectItem value="all">Neuf + Occasion</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="input-label">Catégorie</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="input-field h-12 text-base" data-testid="filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pairs">Paires</SelectItem>
                <SelectItem value="singles">Seuls</SelectItem>
                <SelectItem value="all">Paires + Seuls</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="input-label">Diamètre</label>
            <Select value={diameter} onValueChange={setDiameter}>
              <SelectTrigger className="input-field h-12 text-base" data-testid="filter-diameter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les diamètres</SelectItem>
                {DIAMETERS.map(d => (
                  <SelectItem key={d} value={d.toString()}>R{d}</SelectItem>
                ))}
                <SelectItem value="camionnette">Camionnette (tous diamètres)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          className="w-full h-14 text-lg font-bold bg-[#7C3AED] text-white hover:bg-[#6D28D9] rounded-xl"
          onClick={() => onStart({
            tire_type: tireType === "all" ? null : tireType,
            diameter: diameter === "all" || diameter === "camionnette" ? null : parseInt(diameter),
            category: category === "all" ? null : category,
            camionnette: diameter === "camionnette" ? true : (diameter === "all" ? null : false),
          })}
          data-testid="start-inventory-btn"
        >
          <Play className="w-5 h-5 mr-2" /> Démarrer l&apos;inventaire
        </Button>
      </div>
    </div>
  );
};

// Simple field wrapper
// ==========================================
// STEP 2 — CHECKING (uses SharedAddTireForm from TireFormShared.js)
// ==========================================
const CheckingStep = ({ tires, filters, onComplete, onAddTire, formValues }) => {
  const [checked, setChecked] = useState(() => {
    // Restore checked state from localStorage if available
    const saved = loadInventorySession();
    return saved?.checkedIds ? new Set(saved.checkedIds) : new Set();
  });
  const [showAddForm, setShowAddForm] = useState(false);

  // Persist checked state to localStorage on every change
  useEffect(() => {
    if (tires.length > 0) {
      saveInventorySession({
        step: "checking",
        tires,
        checkedIds: [...checked],
        filters: filters || {},
        savedAt: Date.now(),
      });
    }
  }, [checked, tires]);

  const toggle = (id) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleTireAdded = (newTire) => {
    setShowAddForm(false);
    // Build display fields for the inventory list
    let prefix = "";
    if (newTire.runflat) prefix += "RF";
    if (newTire.camionnette) prefix += "C";
    if (newTire.season === "hiver") prefix += "H";
    else if (newTire.season === "4saisons") prefix += "Q";
    newTire.dimensions = `${prefix}${newTire.width} ${newTire.height} R${newTire.diameter}`;
    newTire.load_speed = `${newTire.load_index || ""}${newTire.speed_index || ""}`;
    onAddTire(newTire);
    setChecked(prev => {
      const next = new Set(prev);
      next.add(newTire.id);
      return next;
    });
    toast.success("Pneu ajouté et pointé");
  };

  // Group by diameter for section headers
  const grouped = useMemo(() => {
    const groups = [];
    let currentDiam = null;
    for (const t of tires) {
      if (t.diameter !== currentDiam) {
        currentDiam = t.diameter;
        groups.push({ type: "header", diameter: currentDiam });
      }
      groups.push({ type: "tire", tire: t });
    }
    return groups;
  }, [tires]);

  return (
    <div data-testid="inventory-checking">
      {/* Progress bar + Add button */}
      <div className="sticky top-14 z-40 bg-white border-b border-[#E5E7EB] px-4 py-3">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-bold text-[#0A0A0A]" data-testid="progress-text">
              {checked.size} / {tires.length} pointés
            </span>
            <Button
              size="sm"
              className="h-8 px-3 bg-[#7C3AED] text-white hover:bg-[#6D28D9] text-xs font-bold"
              onClick={() => setShowAddForm(true)}
              data-testid="inv-add-tire-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un pneu
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#7C3AED] rounded-full transition-all duration-300"
                style={{ width: `${tires.length > 0 ? (checked.size / tires.length * 100) : 0}%` }}
              />
            </div>
            <span className="text-xs text-[#A3A3A3] whitespace-nowrap">
              {tires.length - checked.size} restant{tires.length - checked.size !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Tire list */}
      <div className="max-w-2xl mx-auto px-4 pb-24">
        {grouped.map((item, idx) => {
          if (item.type === "header") {
            return (
              <div key={`h-${item.diameter}`} className="flex items-center gap-2 py-2 mt-3 first:mt-1">
                <span className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">— R{item.diameter} —</span>
                <div className="flex-1 h-px bg-[#E5E7EB]" />
              </div>
            );
          }
          const t = item.tire;
          const isChecked = checked.has(t.id);
          return (
            <button
              key={t.id}
              onClick={() => toggle(t.id)}
              className={`w-full flex items-center gap-3 py-3 px-3 border-b border-[#E5E7EB] text-left transition-colors active:bg-[#F3F4F6] ${
                isChecked ? 'bg-[#F5F3FF]' : 'bg-white'
              }`}
              data-testid={`inv-tire-${t.id}`}
            >
              <div className={`w-7 h-7 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                isChecked ? 'bg-[#7C3AED] border-[#7C3AED]' : 'border-[#D1D5DB] bg-white'
              }`}>
                {isChecked && <CheckCircle className="w-5 h-5 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-mono font-bold text-sm ${isChecked ? 'text-[#7C3AED]' : 'text-[#0A0A0A]'}`}>
                    {t.dimensions} {t.load_speed}
                  </span>
                  <span className="text-sm text-[#525252] truncate">{t.brand} {t.model}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-[#A3A3A3]">x{t.quantity}</span>
                  {t.condition_score && (
                    <span className="text-xs text-[#A3A3A3]">
                      {"★".repeat(t.condition_score)}{"☆".repeat(4 - t.condition_score)}
                    </span>
                  )}
                  {t.nv && <span className="text-[9px] font-bold text-[#DC2626] border border-[#DC2626] rounded px-0.5">[NV]</span>}
                  <span className="text-xs text-[#A3A3A3]">{t.tire_type === "new" ? "N" : "O"}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E7EB] p-4 z-50">
        <div className="max-w-2xl mx-auto">
          <Button
            className="w-full h-14 text-lg font-bold bg-[#7C3AED] text-white hover:bg-[#6D28D9] rounded-xl"
            onClick={() => onComplete(checked)}
            data-testid="finish-inventory-btn"
          >
            <Square className="w-5 h-5 mr-2" /> Terminer l&apos;inventaire
          </Button>
        </div>
      </div>

      {/* Add Tire Dialog — uses exact same form as main app */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] flex flex-col" data-testid="inv-add-tire-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4" /> Ajouter un pneu</DialogTitle>
            <DialogDescription className="sr-only">Ajout de pneu pendant inventaire</DialogDescription>
          </DialogHeader>
          <div className="px-6 pt-2 pb-6 overflow-y-auto flex-1 min-h-0">
            <SharedAddTireForm
              onClose={() => setShowAddForm(false)}
              formValues={formValues}
              submitLabel="Ajouter et pointer"
              onTireCreated={handleTireAdded}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==========================================
// STEP 3 — RESULTS (Missing tires)
// ==========================================
const ResultsStep = ({ tires, checkedIds, filters, addedTireIds, startedAt, onRestart }) => {
  const missingTires = useMemo(() => tires.filter(t => !checkedIds.has(t.id)), [tires, checkedIds]);
  const [selectedForTrash, setSelectedForTrash] = useState(() => new Set(missingTires.map(t => t.id)));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reportDone, setReportDone] = useState(false);

  const toggleTrash = (id) => {
    setSelectedForTrash(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleTrash = async () => {
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/inventory/complete`, {
        checked_tire_ids: [...checkedIds],
        all_tire_ids: tires.map(t => t.id),
        filters,
        added_tire_ids: addedTireIds || [],
        started_at: startedAt || new Date().toISOString(),
      }, { withCredentials: true });

      setReportDone(true);
      toast.success(`Inventaire terminé. ${res.data.total_trashed} pneu(s) envoyé(s) à la corbeille.`);
    } catch (e) {
      toast.error("Erreur lors de la finalisation");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (reportDone) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center" data-testid="inventory-report">
        <div className="w-16 h-16 bg-[#16A34A] rounded-xl flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold text-[#0A0A0A] mb-2">Inventaire terminé</h2>
        <div className="space-y-1 text-sm text-[#525252] mb-6">
          <p>Total audité : <strong>{tires.length}</strong> pneus</p>
          <p>Confirmés présents : <strong className="text-[#16A34A]">{checkedIds.size}</strong></p>
          <p>Manquants : <strong className="text-[#DC2626]">{missingTires.length}</strong></p>
          <p>Envoyés en corbeille : <strong>{selectedForTrash.size}</strong></p>
          <p className="text-xs text-[#A3A3A3] mt-2">Les pneus en corbeille seront automatiquement supprimés après 30 jours.</p>
        </div>
        <Button
          className="h-12 px-8 bg-[#7C3AED] text-white hover:bg-[#6D28D9] rounded-xl"
          onClick={onRestart}
          data-testid="new-inventory-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Nouvel inventaire
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 pb-24" data-testid="inventory-results">
      {/* Summary */}
      <div className="flex items-center gap-3 py-4">
        <AlertTriangle className="w-6 h-6 text-[#EAB308] flex-shrink-0" />
        <div>
          <h2 className="text-lg font-bold text-[#0A0A0A]" data-testid="missing-count">
            {missingTires.length} pneu{missingTires.length !== 1 ? 's' : ''} manquant{missingTires.length !== 1 ? 's' : ''} détecté{missingTires.length !== 1 ? 's' : ''}
          </h2>
          <p className="text-xs text-[#525252]">Décochez les pneus que vous souhaitez garder. Les pneus cochés seront envoyés en corbeille.</p>
        </div>
      </div>

      {missingTires.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle className="w-12 h-12 text-[#16A34A] mx-auto mb-3" />
          <p className="text-lg font-bold text-[#16A34A]">Tous les pneus sont présents !</p>
          <Button className="mt-4 h-12 px-8 bg-[#7C3AED] text-white hover:bg-[#6D28D9] rounded-xl" onClick={onRestart}>
            <RotateCcw className="w-4 h-4 mr-2" /> Nouvel inventaire
          </Button>
        </div>
      ) : (
        <>
          {/* Missing tire list */}
          <div className="bg-white border border-[#FECACA] rounded-lg overflow-hidden">
            {missingTires.map(t => {
              const isSelected = selectedForTrash.has(t.id);
              return (
                <button
                  key={t.id}
                  onClick={() => toggleTrash(t.id)}
                  className={`w-full flex items-center gap-3 py-3 px-3 border-b border-[#FECACA] last:border-b-0 text-left transition-colors ${
                    isSelected ? 'bg-[#FEF2F2]' : 'bg-white'
                  }`}
                  data-testid={`missing-tire-${t.id}`}
                >
                  <div className={`w-7 h-7 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-[#DC2626] border-[#DC2626]' : 'border-[#D1D5DB] bg-white'
                  }`}>
                    {isSelected && <Trash2 className="w-4 h-4 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-mono font-bold text-sm text-[#0A0A0A]">
                      {t.dimensions} {t.load_speed}
                    </span>
                    <span className="text-sm text-[#525252] ml-2">{t.brand} {t.model}</span>
                    <span className="text-xs text-[#A3A3A3] ml-2">x{t.quantity}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Bottom action */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E7EB] p-4 z-50">
            <div className="max-w-2xl mx-auto">
              <Button
                className="w-full h-14 text-lg font-bold bg-[#DC2626] text-white hover:bg-[#B91C1C] rounded-xl"
                onClick={handleTrash}
                disabled={isSubmitting}
                data-testid="confirm-trash-btn"
              >
                {isSubmitting ? "Traitement..." : `Supprimer ${selectedForTrash.size} pneu(s) sélectionné(s)`}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ==========================================
// TRASH VIEW
// ==========================================
const TrashView = () => {
  const [trashItems, setTrashItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTrash = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/trash`, { withCredentials: true });
      setTrashItems(res.data);
    } catch (e) { toast.error("Erreur chargement corbeille"); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API_URL}/api/trash/${id}/restore`, {}, { withCredentials: true });
      toast.success("Pneu restauré");
      fetchTrash();
    } catch (e) { toast.error("Erreur restauration"); }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement ce pneu ? Cette action est irréversible.")) return;
    try {
      await axios.delete(`${API_URL}/api/trash/${id}`, { withCredentials: true });
      toast.success("Pneu supprimé définitivement");
      fetchTrash();
    } catch (e) { toast.error("Erreur suppression"); }
  };

  const daysLeft = (permanentDeleteAt) => {
    if (!permanentDeleteAt) return 30;
    const diff = new Date(permanentDeleteAt) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-4" data-testid="trash-view">
      <h2 className="text-lg font-bold text-[#0A0A0A] mb-3">
        <Trash2 className="w-5 h-5 inline mr-2 text-[#DC2626]" />
        Corbeille ({trashItems.length})
      </h2>
      {isLoading ? (
        <div className="text-center py-8 text-[#A3A3A3]">Chargement...</div>
      ) : trashItems.length === 0 ? (
        <div className="text-center py-8 text-[#A3A3A3]" data-testid="trash-empty">La corbeille est vide</div>
      ) : (
        <div className="space-y-2">
          {trashItems.map(t => (
            <div key={t.id} className="bg-white border border-[#FECACA] rounded-lg p-3" data-testid={`trash-item-${t.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono font-bold text-sm">
                    {t.width}/{t.height}R{t.diameter}
                  </span>
                  <span className="text-sm text-[#525252] ml-2">{t.brand} {t.model}</span>
                  <span className="text-xs text-[#A3A3A3] ml-2">x{t.quantity}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="h-8 text-xs text-[#16A34A] border-[#16A34A] hover:bg-[#F0FDF4]"
                    onClick={() => handleRestore(t.id)} data-testid={`restore-${t.id}`}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Restaurer
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs text-[#DC2626] border-[#DC2626] hover:bg-[#FEF2F2]"
                    onClick={() => handlePermanentDelete(t.id)} data-testid={`perm-delete-${t.id}`}>
                    <XCircle className="w-3 h-3 mr-1" /> Supprimer
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-[#A3A3A3]">
                <span>Supprimé le {new Date(t.trashed_at).toLocaleDateString("fr-FR")}</span>
                <span>par {t.trashed_by}</span>
                <span className={`font-bold ${daysLeft(t.permanent_delete_at) <= 7 ? 'text-[#DC2626]' : 'text-[#525252]'}`}>
                  {daysLeft(t.permanent_delete_at)}j restants
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const INVENTORY_STORAGE_KEY = "reco_inventory_session";

const saveInventorySession = (data) => {
  try { localStorage.setItem(INVENTORY_STORAGE_KEY, JSON.stringify(data)); } catch (e) { /* storage full */ }
};
const loadInventorySession = () => {
  try {
    const raw = localStorage.getItem(INVENTORY_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.savedAt && (Date.now() - data.savedAt) > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(INVENTORY_STORAGE_KEY);
      return null;
    }
    return data;
  } catch (e) { return null; }
};
const clearInventorySession = () => {
  try { localStorage.removeItem(INVENTORY_STORAGE_KEY); } catch (e) { /* ignore */ }
};

// ==========================================
// MAIN PAGE
// ==========================================
const InventairePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { formValues } = useFormValues();
  const [step, setStep] = useState("filters");
  const [tires, setTires] = useState([]);
  const [filters, setFilters] = useState({});
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [isLoadingTires, setIsLoadingTires] = useState(false);
  const [hasRestoredSession, setHasRestoredSession] = useState(false);
  const [addedTireIds, setAddedTireIds] = useState([]);
  const [startedAt, setStartedAt] = useState(null);

  // On mount: check for saved session
  useEffect(() => {
    const saved = loadInventorySession();
    if (saved && saved.step === "checking" && saved.tires?.length > 0) {
      setTires(saved.tires);
      setFilters(saved.filters || {});
      setCheckedIds(new Set(saved.checkedIds || []));
      setStep("checking");
      setHasRestoredSession(true);
      toast.success(`Session d'inventaire restaurée — ${saved.checkedIds?.length || 0}/${saved.tires.length} pointés`);
    }
  }, []);

  const handleStart = async (filterParams) => {
    setIsLoadingTires(true);
    try {
      const res = await axios.post(`${API_URL}/api/inventory/start`, filterParams, { withCredentials: true });
      if (res.data.total === 0) {
        toast.error("Aucun pneu ne correspond aux filtres sélectionnés");
        return;
      }
      setTires(res.data.tires);
      setFilters(filterParams);
      setCheckedIds(new Set());
      setAddedTireIds([]);
      setStartedAt(new Date().toISOString());
      setStep("checking");
      clearInventorySession();
      toast.success(`${res.data.total} pneu(s) à auditer`);
    } catch (e) {
      toast.error("Erreur chargement inventaire");
    } finally {
      setIsLoadingTires(false);
    }
  };

  const handleComplete = (checked) => {
    setCheckedIds(checked);
    setStep("results");
    clearInventorySession();
  };

  const handleAddTire = (newTire) => {
    setTires(prev => {
      const updated = [...prev, newTire];
      updated.sort((a, b) => {
        if (b.diameter !== a.diameter) return b.diameter - a.diameter;
        if (b.width !== a.width) return b.width - a.width;
        return b.height - a.height;
      });
      return updated;
    });
    setAddedTireIds(prev => [...prev, newTire.id]);
  };

  const handleRestart = () => {
    setStep("filters");
    setTires([]);
    setCheckedIds(new Set());
    setFilters({});
    setAddedTireIds([]);
    setStartedAt(null);
    clearInventorySession();
  };

  const filterLabel = useMemo(() => {
    const parts = [];
    if (filters.tire_type === "new") parts.push("Neuf");
    else if (filters.tire_type === "used") parts.push("Occasion");
    else parts.push("Neuf + Occasion");
    if (filters.category === "pairs") parts.push("Paires");
    else if (filters.category === "singles") parts.push("Seuls");
    else parts.push("Paires + Seuls");
    if (filters.camionnette === true) parts.push("Camionnette");
    else if (filters.diameter) parts.push(`R${filters.diameter}`);
    else parts.push("Tous diamètres");
    return parts.join(" · ");
  }, [filters]);

  return (
    <div className="min-h-screen bg-[#FAF5FF]" data-testid="inventaire-page">
      {/* Header */}
      <header className="bg-white border-b-2 border-[#7C3AED] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#7C3AED] rounded-sm flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-[#0A0A0A] hidden sm:block">Inventaire</span>
              {step !== "filters" && step !== "trash" && (
                <span className="text-xs text-[#7C3AED] bg-[#F5F3FF] px-2 py-0.5 rounded-full font-medium ml-2">
                  {filterLabel}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => navigate("/")} className="h-8 px-3 border-[#0A0A0A]" data-testid="back-to-reco-btn">
                <Package className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Reco Stock</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setStep("trash")} className="h-8 px-3 border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2]" data-testid="open-trash-btn">
                <Trash2 className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Corbeille</span>
              </Button>
              <span className="text-sm text-[#525252] hidden sm:block">{user?.name}</span>
              <Button variant="ghost" size="sm" onClick={logout} className="text-[#525252] hover:text-[#0A0A0A] h-8 w-8 p-0" data-testid="inv-logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main>
        {isLoadingTires ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="animate-pulse text-[#7C3AED] font-bold">Chargement des pneus...</div>
          </div>
        ) : (
          <>
            {step === "filters" && <FiltersStep onStart={handleStart} />}
            {step === "checking" && <CheckingStep tires={tires} filters={filters} onComplete={handleComplete} onAddTire={handleAddTire} formValues={formValues} />}
            {step === "results" && (
              <ResultsStep
                tires={tires}
                checkedIds={checkedIds}
                filters={filters}
                addedTireIds={addedTireIds}
                startedAt={startedAt}
                onRestart={handleRestart}
              />
            )}
            {step === "trash" && <TrashView />}
          </>
        )}
      </main>
    </div>
  );
};

export default InventairePage;
